import { Suspense, useEffect, useState } from "react";
import { lazyWithRetry as lazy } from "@/lib/lazy-with-retry";
import { BrowserRouter, HashRouter, Navigate, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "next-themes";
// Homepage stays eager, it's the default landing route, lazy-loading it
// would add a Suspense fallback (blank) before FCP. Every other route is
// lazy: each page becomes its own chunk, fetched only when navigated to.
import Index from "./pages/Index.tsx";
import ScrollToTop from "./components/ScrollToTop.tsx";

const Roster = lazy(() => import("./pages/Roster.tsx"));
const Blog = lazy(() => import("./pages/Blog.tsx"));
const BlogPost = lazy(() => import("./pages/BlogPost.tsx"));
const ArchitecturePage = lazy(() => import("./pages/Architecture.tsx"));
const Download = lazy(() => import("./pages/Download.tsx"));
const Changelog = lazy(() => import("./pages/Changelog.tsx"));
const Docs = lazy(() => import("./pages/Docs.tsx"));
const Learn = lazy(() => import("./pages/Learn.tsx"));
const UseCases = lazy(() => import("./pages/UseCases.tsx"));
const ExampleLibrary = lazy(() => import("./pages/ExampleLibrary.tsx"));
const Pricing = lazy(() => import("./pages/Pricing.tsx"));
const Product = lazy(() => import("./pages/Product.tsx"));
const About = lazy(() => import("./pages/About.tsx"));
const Privacy = lazy(() => import("./pages/Privacy.tsx"));
const Terms = lazy(() => import("./pages/Terms.tsx"));
const Contact = lazy(() => import("./pages/Contact.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const Adm = lazy(() => import("./pages/Adm.tsx"));

const MCP = lazy(() => import("./pages/MCP.tsx"));
const MCPDetail = lazy(() => import("./pages/MCPDetail.tsx"));
const Compare = lazy(() => import("./pages/Compare.tsx"));
const CompareDetail = lazy(() => import("./pages/CompareDetail.tsx"));
const Integrations = lazy(() => import("./pages/Integrations.tsx"));
const LanderNew = lazy(() => import("./pages/LanderNew.tsx"));

const AskManeWidget = lazy(() => import("./components/AskManeWidget.tsx"));

// Defer the chat widget mount until after window `load` + idle so its
// JS chunk + the supabase + react-markdown chunks it pulls in don't
// compete with the LCP-critical work on first paint.
const useDeferredMount = () => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    let cancelled = false;
    let idleId: number | undefined;
    const arm = () => {
      const idle =
        (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback ||
        ((cb: () => void) => setTimeout(cb, 1500));
      idleId = idle(() => { if (!cancelled) setReady(true); }, { timeout: 2000 }) as number;
    };
    if (document.readyState === "interactive" || document.readyState === "complete") arm();
    else document.addEventListener("DOMContentLoaded", arm, { once: true });
    return () => {
      cancelled = true;
      const cancel = (window as unknown as { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback;
      if (cancel && typeof idleId === "number") cancel(idleId);
    };
  }, []);
  return ready;
};

const DeferredAskManeWidget = () => {
  const ready = useDeferredMount();
  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <AskManeWidget />
    </Suspense>
  );
};

// Use HashRouter when running under Electron (file:// can't serve clean URLs).
// Detected by protocol so the same bundle works in both web and desktop.
const isFileProtocol =
  typeof window !== "undefined" && window.location.protocol === "file:";
const Router = isFileProtocol ? HashRouter : BrowserRouter;

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
    <Router>
      <ScrollToTop />
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/agents" element={<Roster />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/product" element={<Product />} />
          <Route path="/architecture" element={<ArchitecturePage />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/get-mane" element={<Download />} />
          <Route path="/download" element={<Navigate to="/get-mane" replace />} />
          <Route path="/changelog" element={<Changelog />} />
          <Route path="/docs" element={<Docs />} />
          <Route path="/docs/:slug" element={<Docs />} />
          <Route path="/learn" element={<Learn />} />
          <Route path="/learn/:slug" element={<Learn />} />
          <Route path="/use-cases" element={<UseCases />} />
          <Route path="/use-cases/:slug" element={<UseCases />} />
          <Route path="/examples" element={<ExampleLibrary />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/_adm_" element={<Adm />} />
          <Route path="/sophia" element={<Navigate to="/blog/introducing-mane-agentic" replace />} />
          <Route path="/mane" element={<Navigate to="/blog/introducing-mane-agentic" replace />} />
          <Route path="/mcp" element={<MCP />} />
          <Route path="/mcp/:slug" element={<MCPDetail />} />
          <Route path="/compare" element={<Compare />} />
          <Route path="/compare/:slug" element={<CompareDetail />} />
          <Route path="/integrations" element={<Integrations />} />
          <Route path="/lander-new" element={<LanderNew />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <DeferredAskManeWidget />
    </Router>
  </ThemeProvider>
);

export default App;
